import { useState } from 'react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import {
  FileText,
  Loader2,
  Sparkles,
  TrendingUp,
  Target,
  Lightbulb,
  Calendar,
  Star,
  Download,
  ChevronDown,
  ChevronUp,
  RefreshCw,
  Trash2,
  Pencil,
  Save,
  X,
  Plus
} from 'lucide-react'
import { ReviewSynthesis } from '@/services/aiService'
import { growthPlanName, type GrowthPlanViewer } from '@/lib/growthPlanWording'

interface ReviewSynthesisReportProps {
  /** Who is reading: the People Leader reads "Growth Plan", the Team Member "My Personalised Growth". */
  viewerRole: GrowthPlanViewer
  synthesis: ReviewSynthesis | null
  isLoading: boolean
  onGenerateSynthesis: () => void
  onExportPDF?: () => void
  /** Remove the stored report (clears it). When absent, no remove button shows. */
  onDeleteSynthesis?: () => void
  /** Persist a manually-edited report. When absent, no edit button shows. */
  onSaveSynthesis?: (edited: ReviewSynthesis) => void
  isFormalReview?: boolean
  isReadOnly?: boolean
}

export function ReviewSynthesisReport({
  viewerRole,
  synthesis,
  isLoading,
  onGenerateSynthesis,
  onExportPDF,
  onDeleteSynthesis,
  onSaveSynthesis,
  isFormalReview = true,
  isReadOnly = false
}: ReviewSynthesisReportProps) {
  const synthesisLabel = 'Check-in Synthesis'
  const planName = growthPlanName(viewerRole)
  const [isCollapsed, setIsCollapsed] = useState(true)
  const [confirmingRemove, setConfirmingRemove] = useState(false)
  // Edit mode: a mutable draft of the report; committed via onSaveSynthesis.
  const [isEditing, setIsEditing] = useState(false)
  const [draft, setDraft] = useState<ReviewSynthesis | null>(null)

  const startEditing = () => {
    if (!synthesis) return
    // Deep clone so edits don't mutate the live object until saved.
    setDraft(JSON.parse(JSON.stringify(synthesis)))
    setIsCollapsed(false)
    setIsEditing(true)
  }
  const cancelEditing = () => {
    setDraft(null)
    setIsEditing(false)
  }
  const saveEditing = () => {
    if (draft && onSaveSynthesis) onSaveSynthesis(draft)
    setIsEditing(false)
  }
  // Update a string-array field (keyAccomplishments / careerTrajectoryInsights / managerNotes)
  const editStrArray = (field: keyof ReviewSynthesis, idx: number, value: string) => {
    setDraft(d => {
      if (!d) return d
      const arr = [...((d[field] as unknown as string[]) || [])]
      arr[idx] = value
      return { ...d, [field]: arr } as ReviewSynthesis
    })
  }
  const removeStrArrayItem = (field: keyof ReviewSynthesis, idx: number) => {
    setDraft(d => {
      if (!d) return d
      const arr = [...((d[field] as unknown as string[]) || [])]
      arr.splice(idx, 1)
      return { ...d, [field]: arr } as ReviewSynthesis
    })
  }
  const addStrArrayItem = (field: keyof ReviewSynthesis) => {
    setDraft(d => {
      if (!d) return d
      const arr = [...((d[field] as unknown as string[]) || []), '']
      return { ...d, [field]: arr } as ReviewSynthesis
    })
  }
  // Update / remove an object-array item (strengthPatterns / developmentPriorities / idpSuggestions)
  const editObjItem = (field: keyof ReviewSynthesis, idx: number, patch: Record<string, any>) => {
    setDraft(d => {
      if (!d) return d
      const arr = [...((d[field] as unknown as any[]) || [])]
      arr[idx] = { ...arr[idx], ...patch }
      return { ...d, [field]: arr } as ReviewSynthesis
    })
  }
  const removeObjItem = (field: keyof ReviewSynthesis, idx: number) => {
    setDraft(d => {
      if (!d) return d
      const arr = [...((d[field] as unknown as any[]) || [])]
      arr.splice(idx, 1)
      return { ...d, [field]: arr } as ReviewSynthesis
    })
  }
  const addObjItem = (field: keyof ReviewSynthesis, blank: Record<string, any>) => {
    setDraft(d => {
      if (!d) return d
      const arr = [...((d[field] as unknown as any[]) || []), { ...blank }]
      return { ...d, [field]: arr } as ReviewSynthesis
    })
  }
  // Edit a string-array NESTED inside an object item (e.g. developmentPriorities[i].suggestedActions)
  const mutateNested = (field: keyof ReviewSynthesis, itemIdx: number, subField: string, fn: (sub: string[]) => string[]) => {
    setDraft(d => {
      if (!d) return d
      const arr = [...((d[field] as unknown as any[]) || [])]
      arr[itemIdx] = { ...arr[itemIdx], [subField]: fn([...((arr[itemIdx][subField] as string[]) || [])]) }
      return { ...d, [field]: arr } as ReviewSynthesis
    })
  }
  // Edit one bucket of the follow-up timeline (30 / 90 / 180-day string lists).
  const mutateTimeline = (bucket: '30-day' | '90-day' | '180-day', fn: (arr: string[]) => string[]) => {
    setDraft(d => {
      if (!d) return d
      const tl = { ...((d.followUpTimeline as any) || { '30-day': [], '90-day': [], '180-day': [] }) }
      tl[bucket] = fn([...((tl[bucket] as string[]) || [])])
      return { ...d, followUpTimeline: tl } as ReviewSynthesis
    })
  }

  const getImportanceColor = (importance: string) => {
    switch (importance) {
      case 'high':
        return 'bg-red-100 text-red-800 border-red-200'
      case 'medium':
        return 'bg-amber-100 text-amber-800 border-amber-200'
      case 'low':
        return 'bg-blue-100 text-blue-800 border-blue-200'
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200'
    }
  }

  const getTimelineColor = (timeline: string) => {
    switch (timeline) {
      case '30-day':
        return 'bg-green-100 text-green-800 border-green-200'
      case '90-day':
        return 'bg-blue-100 text-blue-800 border-blue-200'
      case '180-day':
        return 'bg-purple-100 text-purple-800 border-purple-200'
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200'
    }
  }

  const getRatingLabel = (rating: string) => {
    switch (rating) {
      case 'exceptional':
        return 'Exceptional'
      case 'full':
        return 'Full/Proficient'
      case 'not-yet-full':
        return 'Not-Yet-Full/Developing'
      case 'weak':
        return 'Weak/Inadequate'
      default:
        return rating
    }
  }

  const getRatingColor = (rating: string) => {
    switch (rating) {
      case 'exceptional':
        return 'bg-green-100 text-green-800 border-green-200'
      case 'full':
        return 'bg-blue-100 text-blue-800 border-blue-200'
      case 'not-yet-full':
        return 'bg-amber-100 text-amber-800 border-amber-200'
      case 'weak':
        return 'bg-red-100 text-red-800 border-red-200'
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200'
    }
  }

  // Editable form shown in place of the read-only cards while editing.
  const renderEditForm = () => {
    if (!draft) return null
    const sectionHeader = (title: string) => (
      <h4 className="font-semibold text-gray-800 text-sm">{title}</h4>
    )
    const strSection = (label: string, field: keyof ReviewSynthesis) => {
      const arr = (draft[field] as unknown as string[]) || []
      return (
        <div className="space-y-2">
          {sectionHeader(label)}
          {arr.map((val, i) => (
            <div key={i} className="flex items-start gap-2">
              <textarea
                value={val}
                onChange={e => editStrArray(field, i, e.target.value)}
                className="flex-1 text-sm border border-gray-300 rounded p-2 min-h-[52px]"
              />
              <Button size="sm" variant="ghost" onClick={() => removeStrArrayItem(field, i)} className="text-red-500 shrink-0">
                <X className="h-4 w-4" />
              </Button>
            </div>
          ))}
          <Button size="sm" variant="outline" onClick={() => addStrArrayItem(field)} className="text-xs">
            <Plus className="h-3 w-3 mr-1" /> Add point
          </Button>
        </div>
      )
    }
    // Editable string-list nested inside one object item (e.g. a priority's suggestedActions).
    const nestedStrEditor = (field: keyof ReviewSynthesis, itemIdx: number, subField: string, label: string, item: any) => {
      const arr = (item[subField] as string[]) || []
      return (
        <div className="space-y-1">
          <p className="text-xs font-semibold text-gray-600">{label}</p>
          {arr.map((val, k) => (
            <div key={k} className="flex items-center gap-2">
              <input
                value={val}
                onChange={e => mutateNested(field, itemIdx, subField, sub => { sub[k] = e.target.value; return sub })}
                className="flex-1 text-xs border border-gray-300 rounded p-1.5"
              />
              <Button
                size="sm"
                variant="ghost"
                onClick={() => mutateNested(field, itemIdx, subField, sub => { sub.splice(k, 1); return sub })}
                className="text-red-400 shrink-0 h-7 w-7 p-0"
              >
                <X className="h-3 w-3" />
              </Button>
            </div>
          ))}
          <Button
            size="sm"
            variant="ghost"
            onClick={() => mutateNested(field, itemIdx, subField, sub => [...sub, ''])}
            className="text-xs text-indigo-600 h-7 px-2"
          >
            <Plus className="h-3 w-3 mr-1" /> Add {label.toLowerCase()}
          </Button>
        </div>
      )
    }
    // An object-array section. `extraFields` exposes each item's remaining fields:
    //   { kind: 'select', options } | { kind: 'number' } | { kind: 'strlist' }
    const objSection = (
      label: string,
      field: keyof ReviewSynthesis,
      titleKey: string,
      titlePlaceholder: string,
      bodyKey: string,
      bodyPlaceholder: string,
      accent: string,
      blank: Record<string, any>,
      extraFields: Array<{ kind: 'select' | 'number' | 'strlist'; key: string; label: string; options?: string[] }> = [],
    ) => {
      const scalarFields = extraFields.filter(f => f.kind === 'select' || f.kind === 'number')
      const listFields = extraFields.filter(f => f.kind === 'strlist')
      return (
        <div className="space-y-2">
          {sectionHeader(label)}
          {((draft[field] as unknown as any[]) || []).map((item, i) => (
            <div key={i} className={`border rounded p-3 space-y-2 ${accent}`}>
              <div className="flex items-start gap-2">
                <input
                  value={item[titleKey] || ''}
                  onChange={e => editObjItem(field, i, { [titleKey]: e.target.value })}
                  className="flex-1 text-sm font-medium border border-gray-300 rounded p-2"
                  placeholder={titlePlaceholder}
                />
                <Button size="sm" variant="ghost" onClick={() => removeObjItem(field, i)} className="text-red-500 shrink-0">
                  <X className="h-4 w-4" />
                </Button>
              </div>
              {scalarFields.length > 0 && (
                <div className="flex flex-wrap gap-3">
                  {scalarFields.map(f => (
                    <label key={f.key} className="flex items-center gap-1.5 text-xs text-gray-600">
                      {f.label}
                      {f.kind === 'select' ? (
                        <select
                          value={item[f.key] ?? f.options?.[0]}
                          onChange={e => editObjItem(field, i, { [f.key]: e.target.value })}
                          className="text-xs border border-gray-300 rounded p-1 bg-white"
                        >
                          {(f.options || []).map(o => (
                            <option key={o} value={o}>{o}</option>
                          ))}
                        </select>
                      ) : (
                        <input
                          type="number"
                          min={0}
                          value={item[f.key] ?? 0}
                          onChange={e => editObjItem(field, i, { [f.key]: Number(e.target.value) })}
                          className="w-16 text-xs border border-gray-300 rounded p-1"
                        />
                      )}
                    </label>
                  ))}
                </div>
              )}
              <textarea
                value={item[bodyKey] || ''}
                onChange={e => editObjItem(field, i, { [bodyKey]: e.target.value })}
                className="w-full text-sm border border-gray-300 rounded p-2 min-h-[52px]"
                placeholder={bodyPlaceholder}
              />
              {listFields.map(f => (
                <div key={f.key}>{nestedStrEditor(field, i, f.key, f.label, item)}</div>
              ))}
            </div>
          ))}
          <Button size="sm" variant="outline" onClick={() => addObjItem(field, blank)} className="text-xs">
            <Plus className="h-3 w-3 mr-1" /> Add point
          </Button>
        </div>
      )
    }
    // Follow-up timeline: three fixed buckets, each an editable string list.
    const timelineEditor = () => {
      const buckets: Array<{ key: '30-day' | '90-day' | '180-day'; label: string }> = [
        { key: '30-day', label: '30-Day Actions' },
        { key: '90-day', label: '90-Day Milestones' },
        { key: '180-day', label: '180-Day Goals' },
      ]
      const tl = (draft.followUpTimeline as any) || { '30-day': [], '90-day': [], '180-day': [] }
      return (
        <div className="space-y-3">
          {sectionHeader('📅 Follow-Up Timeline')}
          {buckets.map(b => {
            const arr = (tl[b.key] as string[]) || []
            return (
              <div key={b.key} className="space-y-1 border rounded p-3 border-cyan-200 bg-cyan-50/40">
                <p className="text-xs font-semibold text-cyan-900">{b.label}</p>
                {arr.map((val, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <input
                      value={val}
                      onChange={e => mutateTimeline(b.key, sub => { sub[i] = e.target.value; return sub })}
                      className="flex-1 text-xs border border-gray-300 rounded p-1.5"
                    />
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => mutateTimeline(b.key, sub => { sub.splice(i, 1); return sub })}
                      className="text-red-400 shrink-0 h-7 w-7 p-0"
                    >
                      <X className="h-3 w-3" />
                    </Button>
                  </div>
                ))}
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => mutateTimeline(b.key, sub => [...sub, ''])}
                  className="text-xs text-indigo-600 h-7 px-2"
                >
                  <Plus className="h-3 w-3 mr-1" /> Add action
                </Button>
              </div>
            )
          })}
        </div>
      )
    }
    return (
      <div className="space-y-6">
        <p className="text-xs text-gray-600 bg-amber-50 border border-amber-200 rounded p-2">
          Editing the report — reword or remove any point, then <strong>Save</strong>. Saved reports are marked “Edited”.
        </p>
        {strSection('🏆 Key Accomplishments', 'keyAccomplishments')}
        {objSection('🎯 Development Priorities', 'developmentPriorities', 'area', 'Area', 'rationale', 'Rationale', 'border-amber-200 bg-amber-50/40',
          { area: '', importance: 'medium', rationale: '', suggestedActions: [], timeline: '30-day' },
          [
            { kind: 'select', key: 'importance', label: 'Priority', options: ['high', 'medium', 'low'] },
            { kind: 'select', key: 'timeline', label: 'Timeline', options: ['30-day', '90-day', '180-day'] },
            { kind: 'strlist', key: 'suggestedActions', label: 'Suggested actions' },
          ])}
        {objSection('📊 Strength Patterns', 'strengthPatterns', 'theme', 'Theme', 'description', 'Description', 'border-blue-200 bg-blue-50/40',
          { theme: '', description: '', supportingKPAs: [], evidenceCount: 0 },
          [
            { kind: 'number', key: 'evidenceCount', label: 'Impact portfolio items' },
            { kind: 'strlist', key: 'supportingKPAs', label: 'Supporting KPAs' },
          ])}
        {objSection(`💡 ${planName} Suggestions`, 'idpSuggestions', 'focusArea', 'Focus area', 'developmentGoal', 'Development goal', 'border-purple-200 bg-purple-50/40',
          { focusArea: '', developmentGoal: '', suggestedActivities: [], successMetrics: [], resources: [] },
          [
            { kind: 'strlist', key: 'suggestedActivities', label: 'Activities' },
            { kind: 'strlist', key: 'successMetrics', label: 'Success metrics' },
            { kind: 'strlist', key: 'resources', label: 'Resources' },
          ])}
        {strSection('🚀 Career Trajectory Insights', 'careerTrajectoryInsights')}
        {timelineEditor()}
        {strSection('📝 Notes to Remember', 'managerNotes')}
      </div>
    )
  }

  return (
    <Card className="border-2 border-indigo-200 bg-gradient-to-br from-indigo-50 to-purple-50">
      <CardHeader className="pb-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 flex-1">
            <FileText className="h-5 w-5 text-indigo-600" />
            <CardTitle className="text-indigo-900">{synthesisLabel} & {planName}</CardTitle>
            {synthesis && ((synthesis as any).manuallyEdited ? (
              <Badge className="text-xs bg-slate-100 text-slate-700 border border-slate-200">
                Edited
              </Badge>
            ) : (
              <Badge className="text-xs bg-purple-100 text-purple-800 border border-purple-200">
                AI-Generated Summary
              </Badge>
            ))}
          </div>
          <div className="flex items-center gap-2">
            {synthesis && (
              <Button
                size="sm"
                variant="ghost"
                onClick={() => setIsCollapsed(!isCollapsed)}
              >
                {isCollapsed ? (
                  <ChevronDown className="h-4 w-4" />
                ) : (
                  <ChevronUp className="h-4 w-4" />
                )}
              </Button>
            )}
            {synthesis && isEditing && (
              <>
                <Button size="sm" variant="default" onClick={saveEditing} className="bg-indigo-600 hover:bg-indigo-700">
                  <Save className="h-4 w-4 mr-1" />
                  Save
                </Button>
                <Button size="sm" variant="ghost" onClick={cancelEditing}>
                  <X className="h-4 w-4 mr-1" />
                  Cancel
                </Button>
              </>
            )}
            {synthesis && !isEditing && (
              <>
                {!isReadOnly && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={onGenerateSynthesis}
                    disabled={isLoading}
                    className="border-indigo-300 text-indigo-700"
                  >
                    {isLoading ? (
                      <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                    ) : (
                      <RefreshCw className="h-4 w-4 mr-1" />
                    )}
                    Regenerate
                  </Button>
                )}
                {!isReadOnly && onSaveSynthesis && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={startEditing}
                    className="border-indigo-300 text-indigo-700"
                  >
                    <Pencil className="h-4 w-4 mr-1" />
                    Edit
                  </Button>
                )}
                <Button
                  size="sm"
                  variant="outline"
                  onClick={onExportPDF}
                  className="border-indigo-300 text-indigo-700"
                >
                  <Download className="h-4 w-4 mr-1" />
                  Export
                </Button>
                {!isReadOnly && onDeleteSynthesis && (
                  confirmingRemove ? (
                    <>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => { onDeleteSynthesis(); setConfirmingRemove(false) }}
                        className="border-red-300 text-red-700 hover:bg-red-50"
                      >
                        <Trash2 className="h-4 w-4 mr-1" />
                        Confirm remove
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => setConfirmingRemove(false)}>
                        Cancel
                      </Button>
                    </>
                  ) : (
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => setConfirmingRemove(true)}
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                    >
                      <Trash2 className="h-4 w-4 mr-1" />
                      Remove
                    </Button>
                  )
                )}
              </>
            )}
            {!synthesis && (
              <Button
                size="sm"
                variant="default"
                onClick={onGenerateSynthesis}
                disabled={isLoading}
                className="bg-indigo-600 hover:bg-indigo-700"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Generating...
                  </>
                ) : (
                  <>
                    <Sparkles className="h-4 w-4 mr-2" />
                    Generate Synthesis
                  </>
                )}
              </Button>
            )}
          </div>
        </div>
      </CardHeader>

      {/* Collapsed Summary */}
      {synthesis && isCollapsed && !isEditing && (
        <CardContent className="pt-3 pb-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {isFormalReview && synthesis.overallRating && (
                <Badge className={`text-sm px-3 py-1 ${getRatingColor(synthesis.overallRating)}`}>
                  {getRatingLabel(synthesis.overallRating)}
                </Badge>
              )}
              <div className="text-sm space-y-1">
                <div>
                  <span className="font-medium text-gray-900">
                    {synthesis.keyAccomplishments.length} key accomplishments
                  </span>
                  <span className="text-gray-600 mx-2">•</span>
                  <span className="text-gray-600">
                    {synthesis.strengthPatterns.length} strength patterns
                  </span>
                  <span className="text-gray-600 mx-2">•</span>
                  <span className="text-gray-600">
                    {synthesis.developmentPriorities.length} development priorities
                  </span>
                </div>
                {synthesis.idpSuggestions.length > 0 && (
                  <div className="text-indigo-700 font-medium">
                    {synthesis.idpSuggestions.length} {planName} suggestions
                  </div>
                )}
              </div>
            </div>
            <Button 
              size="sm" 
              variant="ghost" 
              onClick={() => setIsCollapsed(false)}
              className="text-indigo-600 hover:text-indigo-700"
            >
              View Full Synthesis
            </Button>
          </div>
        </CardContent>
      )}

      {/* Expanded Content */}
      {synthesis && !isCollapsed && (
        <CardContent className="space-y-6 max-h-[700px] overflow-y-auto">
          {isEditing && draft ? renderEditForm() : (<>
          {/* Header Summary */}
          <div className="bg-white rounded-lg p-4 border-2 border-blue-200">
            <div className={`grid grid-cols-1 ${isFormalReview && synthesis.overallRating ? 'sm:grid-cols-3' : 'sm:grid-cols-2'} gap-3 md:gap-4`}>
              <div>
                <p className="text-sm text-gray-600 mb-1">Team Member</p>
                <p className="font-semibold text-gray-900">{synthesis.employeeName}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600 mb-1">Check-in Type</p>
                <p className="font-semibold text-gray-900 capitalize">{synthesis.reviewType.replace(/-/g, ' ').replace('checkin', 'Check-in')}</p>
              </div>
              {isFormalReview && synthesis.overallRating && (
                <div>
                  <p className="text-sm text-gray-600 mb-1">Overall Impact & Growth Indicator</p>
                  <Badge className={`${getRatingColor(synthesis.overallRating)}`}>
                    {getRatingLabel(synthesis.overallRating)}
                  </Badge>
                </div>
              )}
            </div>
          </div>

          {/* Key Accomplishments */}
          {synthesis.keyAccomplishments.length > 0 && (
            <div className="space-y-3">
              <h4 className="font-semibold text-green-700 flex items-center gap-2">
                <Star className="h-5 w-5" />
                🏆 Top 5 Key Accomplishments
              </h4>
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 space-y-3">
                {synthesis.keyAccomplishments.map((accomplishment, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <div className="bg-green-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold shrink-0">
                      {i + 1}
                    </div>
                    <p className="text-sm text-green-900 pt-0.5">{accomplishment}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Strength Patterns */}
          {synthesis.strengthPatterns.length > 0 && (
            <div className="space-y-3">
              <h4 className="font-semibold text-blue-700 flex items-center gap-2">
                <TrendingUp className="h-5 w-5" />
                📊 Strength Patterns Across KPAs
              </h4>
              <div className="space-y-3">
                {synthesis.strengthPatterns.map((pattern, i) => (
                  <div key={i} className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <div className="flex items-start justify-between mb-2">
                      <h5 className="font-semibold text-blue-900">{pattern.theme}</h5>
                      <Badge variant="outline" className="text-xs">
                        {pattern.evidenceCount} impact portfolio items
                      </Badge>
                    </div>
                    <p className="text-sm text-blue-800 mb-3">{pattern.description}</p>
                    <div className="flex flex-wrap gap-2">
                      {pattern.supportingKPAs.map((kpa, j) => (
                        <Badge key={j} className="text-xs bg-blue-100 text-blue-800 border border-blue-200">
                          {kpa}
                        </Badge>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Development Priorities */}
          {synthesis.developmentPriorities.length > 0 && (
            <div className="space-y-3">
              <h4 className="font-semibold text-amber-700 flex items-center gap-2">
                <Target className="h-5 w-5" />
                🎯 Development Priorities
              </h4>
              <div className="space-y-3">
                {synthesis.developmentPriorities.map((priority, i) => (
                  <div key={i} className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                    <div className="flex items-start justify-between mb-2">
                      <h5 className="font-semibold text-amber-900">{priority.area}</h5>
                      <div className="flex gap-2">
                        <Badge className={getImportanceColor(priority.importance)}>
                          {priority.importance} priority
                        </Badge>
                        <Badge className={getTimelineColor(priority.timeline)}>
                          {priority.timeline}
                        </Badge>
                      </div>
                    </div>
                    <p className="text-sm text-amber-800 mb-3 italic">{priority.rationale}</p>
                    <div className="space-y-1">
                      <p className="text-xs font-semibold text-amber-900">Suggested Actions:</p>
                      {priority.suggestedActions.map((action, j) => (
                        <div key={j} className="flex items-start gap-2">
                          <span className="text-amber-600 text-xs mt-0.5">→</span>
                          <p className="text-xs text-amber-900">{action}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* IDP Suggestions */}
          {synthesis.idpSuggestions.length > 0 && (
            <div className="space-y-3">
              <h4 className="font-semibold text-purple-700 flex items-center gap-2">
                <Lightbulb className="h-5 w-5" />
                💡 {planName} Suggestions
              </h4>
              <div className="space-y-3">
                {synthesis.idpSuggestions.map((idp, i) => (
                  <div key={i} className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                    <div className="mb-3">
                      <Badge variant="outline" className="mb-2 text-xs">
                        {idp.focusArea}
                      </Badge>
                      <h5 className="font-semibold text-purple-900">{idp.developmentGoal}</h5>
                    </div>
                    
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 md:gap-4">
                      <div>
                        <p className="text-xs font-semibold text-purple-900 mb-2">Activities:</p>
                        {idp.suggestedActivities.map((activity, j) => (
                          <p key={j} className="text-xs text-purple-800 mb-1">• {activity}</p>
                        ))}
                      </div>
                      <div>
                        <p className="text-xs font-semibold text-purple-900 mb-2">Success Metrics:</p>
                        {idp.successMetrics.map((metric, j) => (
                          <p key={j} className="text-xs text-purple-800 mb-1">✓ {metric}</p>
                        ))}
                      </div>
                      <div>
                        <p className="text-xs font-semibold text-purple-900 mb-2">Resources:</p>
                        {idp.resources.map((resource, j) => (
                          <p key={j} className="text-xs text-purple-800 mb-1">📚 {resource}</p>
                        ))}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Career Trajectory Insights */}
          {synthesis.careerTrajectoryInsights.length > 0 && (
            <div className="space-y-3">
              <h4 className="font-semibold text-indigo-700 flex items-center gap-2">
                <TrendingUp className="h-5 w-5" />
                🚀 Career Trajectory Insights
              </h4>
              <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4 space-y-2">
                {synthesis.careerTrajectoryInsights.map((insight, i) => (
                  <div key={i} className="flex items-start gap-2">
                    <span className="text-indigo-600 mt-0.5 font-bold">•</span>
                    <p className="text-sm text-indigo-900">{insight}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Follow-Up Timeline */}
          <div className="space-y-3">
            <h4 className="font-semibold text-cyan-700 flex items-center gap-2">
              <Calendar className="h-5 w-5" />
              📅 Follow-Up Timeline
            </h4>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 md:gap-4">
              {/* 30-day */}
              {synthesis.followUpTimeline['30-day']?.length > 0 && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <Badge className="mb-3 bg-green-600 text-white">
                    30-Day Actions
                  </Badge>
                  <div className="space-y-2">
                    {synthesis.followUpTimeline['30-day'].map((action, i) => (
                      <p key={i} className="text-xs text-green-900">• {action}</p>
                    ))}
                  </div>
                </div>
              )}

              {/* 90-day */}
              {synthesis.followUpTimeline['90-day']?.length > 0 && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <Badge className="mb-3 bg-blue-600 text-white">
                    90-Day Milestones
                  </Badge>
                  <div className="space-y-2">
                    {synthesis.followUpTimeline['90-day'].map((action, i) => (
                      <p key={i} className="text-xs text-blue-900">• {action}</p>
                    ))}
                  </div>
                </div>
              )}

              {/* 180-day */}
              {synthesis.followUpTimeline['180-day']?.length > 0 && (
                <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                  <Badge className="mb-3 bg-purple-600 text-white">
                    180-Day Goals
                  </Badge>
                  <div className="space-y-2">
                    {synthesis.followUpTimeline['180-day'].map((action, i) => (
                      <p key={i} className="text-xs text-purple-900">• {action}</p>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Timestamp */}
          <div className="text-xs text-gray-500 text-center pt-4 border-t">
            Generated at {new Date(synthesis.generatedAt).toLocaleString()}
          </div>
          </>)}
        </CardContent>
      )}

      {!synthesis && !isLoading && (
        <CardContent>
          <div className="text-center py-8 space-y-4">
            <p className="text-sm text-blue-700">
              Generate a comprehensive check-in summary with AI-powered development recommendations
            </p>
            <div className="flex flex-wrap justify-center gap-2 text-xs text-gray-600">
              <Badge variant="outline">Top Accomplishments</Badge>
              <Badge variant="outline">Strength Patterns</Badge>
              <Badge variant="outline">Development Priorities</Badge>
              <Badge variant="outline">{planName} Suggestions</Badge>
              <Badge variant="outline">Follow-Up Timeline</Badge>
            </div>
          </div>
        </CardContent>
      )}
    </Card>
  )
}
